import { safeStringify } from '@jira/platform__error-reporting/src/safeStringify.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import { safeErrorWithoutCustomerData, SHOULD_LOG_ERROR } from './shared.tsx';

export type LoadBridgeBailOptions = {
	/**
	 * AMD name of the resource being loaded.
	 */
	name: string;
	shouldLogError?: boolean;
};

/**
 * Load an AMD module if the system has already loaded it, otherwise throw.
 * This is a weak load without retries.
 * It is only safe to use where there is some previous load of WRM that includes the required AMD
 * module.
 *
 * IMPORTANT - Does *not* support modules with nested definitions.
 * @see loadBridgeRace
 * @throws Error
 */
export const loadBridgeBail = async <T,>({
	name,
	shouldLogError = SHOULD_LOG_ERROR,
}: LoadBridgeBailOptions): Promise<T> => {
	try {
		/* oxlint-disable typescript/consistent-type-assertions */
		const browserWindow = getWindow();
		if (!browserWindow) {
			throw new Error('Browser window is unavailable');
		}
		const requireModule = Reflect.get(browserWindow, 'require');
		if (typeof requireModule !== 'function') {
			throw new Error('Browser AMD loader is unavailable');
		}
		return requireModule.call(browserWindow, name) as T;
		/* oxlint-enable typescript/consistent-type-assertions */
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (shouldLogError) {
			safeErrorWithoutCustomerData(
				'common.bridge',
				`Unable to load bridge "${name}". ${safeStringify(error) ?? ''}`,
				{
					isSpa: __SPA__,
					name,
					error: safeStringify(error),
				},
			);
		}
		throw new Error(`Unable to load bridge "${name}"`, { cause: error });
	}
};
